import React, { useContext, useState } from 'react';
import TemplateEditorContext from '../../../contexts/TemplateEditorContext';
import { useSavePdfLayout } from '../../../hooks/pdfLayout';
import {
  PdfLayout,
  PdfLayoutGroup,
  PdfLayoutSection,
} from '../../../utils/api/pdfLayout';
import DocumentPreview from './DocumentPreview';
import { Modal } from '@stormwater-software/lib-ui';

const ZOOM_MIN = 50;
const ZOOM_MAX = 150;
const ZOOM_STEP = 10;

export const defaultLayout = (): PdfLayout => ({
  header_config: {
    enabled: false,
    mode: 'text',
    border: 'none',
    repeat: 'all',
    justifyContent: 'left',
    isJustifySeperate: false,
    textStyle: { justifyContent: 'left', backgroundColor: '#ffffff' },
    logoJustifyContent: 'left',
    text: '',
  },
  footer_config: { enabled: false, text: '', repeat: 'all' },
  certifications_config: { placement: 'default', after_group_id: null },
  findings_config: { placement: 'default', after_group_id: null },
  findings_log_style_config: {
    header_style: {},
    split_open_closed: false,
    columns: { initiated_by: false, closed_by: false },
  },
  certification_style_config: { header_style: {} },
  show_borders: false,
  page_number_position: 'none',
  orientation: 'portrait',
  margins: 'normal',
});

interface Props {
  templateName: string;
  layout: PdfLayout;
  sections: Record<number, PdfLayoutSection>;
  groups: PdfLayoutGroup[];
  logoGuid: string | null;
  dirty: boolean;
  setDirty: (dirty: boolean) => void;
  collapsed: boolean;
  onPatchLayout: (fields: Partial<PdfLayout>) => void;
  onPatchSection: (groupId: number, fields: Partial<PdfLayoutSection>) => void;
}

/** PDF layout controls + live document preview (right pane of the builder) */
const PdfPanel: React.FC<Props> = ({
  templateName,
  layout,
  sections,
  groups,
  logoGuid,
  dirty,
  setDirty,
  collapsed,
  onPatchLayout,
  onPatchSection,
}) => {
  const { templateId } = useContext(TemplateEditorContext);
  const saveMutation = useSavePdfLayout(templateId);

  const [zoom, setZoom] = useState(100);
  const [modalView, setModalView] = useState(false);
  const handleSave = () => {
    setDirty(false);
    saveMutation.mutate({ ...layout, sections: Object.values(sections) });
  };

  const status = saveMutation.isLoading
    ? { className: '', text: 'Saving…', icon: 'fa-spinner fa-spin' }
    : dirty
      ? { className: '', text: 'Unsaved changes', icon: 'fa-exclamation-circle' }
      : saveMutation.isError
        ? { className: 'is-error', text: 'Failed to save', icon: 'fa-times-circle' }
        : { className: 'is-saved', text: '✓ All changes saved', icon: 'fa-check-circle' };

  return (
    <section className='utb-layout-pane'>
      <div className='utb-layout-controls'>
        <div className='utb-layout-controls__head'>
          <h4>PDF Layout</h4>
          <span className={`ple-status ${status.className}`} title={status.text}>
            {collapsed ? <i className={`fa ${status.icon}`} /> : status.text}
          </span>
          <button
            type='button'
            className='ple-preview-btn'
            onClick={handleSave}
            disabled={saveMutation.isLoading}
            title='Save Layout'
          >
            {collapsed ? (
              <i className='fa fa-solid fa-save' />
            ) : saveMutation.isLoading ? (
              'Saving…'
            ) : (
              'Save Layout'
            )}
          </button>
          <button
            type='button'
            className='ple-preview-btn-secondary'
            onClick={() => setModalView(true)}
            title='Preview PDF'
          >
            {collapsed ? (
              <i className='fa fa-solid fa-eye' />
            ) : (
              'Preview PDF'
            )}
          </button>
        </div>

        <div className='utb-layout-controls__row'>
          <div className='ple-field'>
            <span className='ple-field__label'>Orientation</span>
            <div className='ple-seg'>
              {(['portrait', 'landscape'] as const).map((orientation) => (
                <button
                  key={orientation}
                  type='button'
                  className={
                    layout.orientation === orientation ? 'is-active' : ''
                  }
                  onClick={() => onPatchLayout({ orientation })}
                >
                  {orientation.charAt(0).toUpperCase() + orientation.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className='ple-field'>
            <span className='ple-field__label'>Margins</span>
            <div className='ple-seg'>
              {['narrow', 'normal', 'wide'].map((margin) => (
                <button
                  key={margin}
                  type='button'
                  className={layout.margins === margin ? 'is-active' : ''}
                  onClick={() =>
                    onPatchLayout({ margins: margin as PdfLayout['margins'] })
                  }
                >
                  {margin.charAt(0).toUpperCase() + margin.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className='ple-field'>
            <span className='ple-field__label'>Page no.</span>
            <select
              className='ple-input'
              value={layout.page_number_position}
              onChange={(e) =>
                onPatchLayout({
                  page_number_position: e.target
                    .value as PdfLayout['page_number_position'],
                })
              }
            >
              <option value='none'>None</option>
              <option value='top'>Top</option>
              <option value='bottom'>Bottom</option>
            </select>
          </div>

          <div className='ple-field'>
            <span className='ple-field__label'>Border</span>
            <label className='ple-toggle'>
              <input
                type='checkbox'
                checked={layout.show_borders}
                onChange={(e) =>
                  onPatchLayout({ show_borders: e.target.checked })
                }
              />
              <span className='ple-toggle__track' />
            </label>
          </div>

          <div className='ple-field'>
            <span className='ple-field__label'>Zoom</span>
            <div className='ple-zoom'>
              <button
                type='button'
                onClick={() =>
                  setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))
                }
              >
                <i className='fa fa-solid fa-minus'></i>
              </button>
              <span>{zoom}%</span>
              <button
                type='button'
                onClick={() =>
                  setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))
                }
              >
                <i className='fa fa-solid fa-plus'></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className='ple-canvas'>
        <div
          className='ple-zoom-frame'
          style={{ zoom: zoom / 100 } as React.CSSProperties}
        >
          <DocumentPreview
            templateName={templateName}
            layout={layout}
            sections={sections}
            groups={groups}
            logoGuid={logoGuid}
            onPatch={onPatchLayout}
            onPatchSection={onPatchSection}
          />
        </div>
      </div>

      <Modal
        show={modalView}
        handleClose={() => setModalView(false)}
        title='PDF Preview'
        dismissOnBgClick={true}
        classes='ple-preview-modal'
      >
        <DocumentPreview
          templateName={templateName}
          layout={layout}
          sections={sections}
          groups={groups}
          logoGuid={logoGuid}
          onPatch={onPatchLayout}
          onPatchSection={onPatchSection}
        />
      </Modal>
    </section>
  );
};

export default PdfPanel;
